import { Injectable } from '@angular/core';
import { NgbModal }   from '@ng-bootstrap/ng-bootstrap';

import { ApiService }                           from '../api/api.service';
import { InvoiceModel }                         from '../invoices/models/invoice';
import { TableQueryParameters }                 from '../table/types/table-query-parameters';
import { VehicleImportedHistoryNotesComponent } from './vehicle.imported-history.notes.component';
import { VehicleModel }                         from './models/vehicle';
import { VehiclesAdvertisingDetailsModel }      from './typings/vehicles.advertising-details';
import { VehiclesAdvertisingModel }             from './typings/vehicles.advertising';
import { VehiclesEventModel }                   from './typings/vehicles.event';
import { VehiclesFiltersModel }                 from './typings/vehicles.filters';
import { VehiclesImportedHistoryModel }         from './typings/vehicles.imported-history';
import { VehiclesInspectionModel }              from './typings/vehicles.inspection';
import { VehiclesTransactionLinkModel }         from './typings/vehicles.transaction-link';
import { VehiclesTransactionModel }             from './typings/vehicles.transaction';
import { WindowService }                        from '../core/services/window.service';

@Injectable({
  providedIn: 'root'
})
export class VehiclesService {
  public broadcastKey                 = 'filterVehicles';
  public eventsTableCacheKey          = 'vehicleEvents';
  public importedHistoryTableCacheKey = 'vehicleHistory';
  public inspectionsTableCacheKey     = 'vehicleInspections';
  public invoicesTableCacheKey        = 'vehicleInvoices';
  public tableCacheKey                = 'vehicles';

  constructor (
    private apiService   : ApiService,
    private modalService : NgbModal,
    private windowService: WindowService
  ) {}

  addAttachmentAdvertising ( details : VehiclesAdvertisingDetailsModel ) : Promise<boolean> {
    return this.apiService.patch('/vehicle_attachment/advertise_all_vehicle_attachments', details, 'vehicle_attachment');
  }

  get ( params : TableQueryParameters, filters : VehiclesFiltersModel ) : Promise<Array<VehicleModel>> {
    return this.apiService.stream('/filters', [
        filters.value || '*',
        'vehicles',
        params.offset,
        params.pageSize,
        params.field,
        params.direction,
        filters.archivedOnly
      ]);
  }

  getCount ( filters : VehiclesFiltersModel ) : Promise<number> {
    return this.apiService.get('/filters/count', [
        filters.value || '*',
        'vehicles',
        filters.archivedOnly
      ]);
  }

  getEvents ( vehicle : VehicleModel, params : TableQueryParameters = {} ) : Promise<Array<VehiclesEventModel>> {
    return this.apiService.get('/vehicles/event_list', [
        vehicle.id,
        params.offset    || 0,
        params.pageSize  || 5,
        params.field     || 'event_date',
        params.direction || 'desc'
      ]);
  }

  getEventsCount ( vehicle : VehicleModel ) : Promise<number> {
    return this.apiService.get('/vehicles/event_count', vehicle.id);
  }

  getImportedHistory ( vehicle : VehicleModel, params : TableQueryParameters = {} ) : Promise<Array<VehiclesImportedHistoryModel>> {
    return this.apiService.get('/vehicles/vehicle_history_list', [
        vehicle.id,
        params.offset    || 0,
        params.pageSize  || 5,
        params.field     || 'post_date',
        params.direction || 'desc'
      ]);
  }

  getImportedHistoryCount ( vehicle : VehicleModel ) : Promise<number> {
    return this.apiService.get('/vehicles/vehicle_history_list_count', vehicle.id);
  }

  getInspections ( vehicle : VehicleModel, params : TableQueryParameters = {} ) : Promise<Array<VehiclesInspectionModel>> {
    return this.apiService.get('/vehicles/inspection_list', [
        vehicle.id,
        params.offset    || 0,
        params.pageSize  || 5,
        params.field     || 'inspection_date',
        params.direction || 'asc'
      ]);
  }

  getInspectionsCount ( vehicle : VehicleModel ) : Promise<number> {
    return this.apiService.get('/vehicles/inspection_count', vehicle.id);
  }

  getInvoices ( vehicle : VehicleModel, params : TableQueryParameters = {} ) : Promise<Array<InvoiceModel>> {
    return this.apiService.get('/vehicles/invoice_list', [
        vehicle.id,
        params.offset    || 0,
        params.pageSize  || 5,
        params.field     || 'post_date',
        params.direction || 'desc'
      ]);
  }

  getInvoicesCount ( vehicle : VehicleModel ) : Promise<number> {
    return this.apiService.get('/vehicles/invoice_count', vehicle.id);
  }

  getTransactionLink ( transaction : VehiclesTransactionModel ) : Promise<VehiclesTransactionLinkModel> {
    return this.apiService.get('/dealership_transaction_logs/link_details_for_log', transaction.id);
  }

  getTransactions ( vehicle : VehicleModel, params : TableQueryParameters = {} ) : Promise<Array<VehiclesTransactionModel>> {
    return this.apiService.get('/dealership_transaction_logs/index_for_vehicle', [
        vehicle.id,
        params.offset   || 0,
        params.pageSize || 5
      ]);
  }

  getTransactionsCount ( vehicle : VehicleModel ) : Promise<number> {
    return this.apiService.get('/dealership_transaction_logs/count_for_vehicle', vehicle.id);
  }

  openInvoice ( invoice : InvoiceModel ) : void {
    this.windowService.open(`${
        this.apiService.getEndpoint()
      }/reports/jasper_invoice/${
        invoice.id
      }.pdf`);
  }

  removeAttachmentAdvertising ( details : VehiclesAdvertisingDetailsModel ) : Promise<boolean> {
    return this.apiService.patch('/vehicle_attachment/remove_advertising_from_vehicle_attachments', details, 'vehicle_attachment');
  }

  saveAdvertising ( advertising : VehiclesAdvertisingModel ) : Promise<Array<VehiclesAdvertisingModel>> {
    return this.apiService.patch('/dealership_advertising_vehicle', advertising, 'dealership_advertising_vehicle');
  }

  viewImportedHistory ( history : VehiclesImportedHistoryModel ) : void {
    this.windowService.open(`${
        this.apiService.getEndpoint()
      }/reports/history_report/${
        history.id
      }.pdf`);
  }

  viewImportedHistoryNotes ( history : VehiclesImportedHistoryModel ) : Promise<void> {
    const modalRef = this.modalService.open(VehicleImportedHistoryNotesComponent, {
        windowClass: 'lg-modal'
      });

    modalRef.componentInstance.history = history;

    return modalRef.result.then(() => undefined, () => undefined);
  }
}
